import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowLeft } from "lucide-react";
import type { ActivityLog } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { dayHeading, describeActivity, whenLabel } from "@/lib/activity";
import { ActivityRow } from "@/components/History";
import { activityHref, useActivityLookups } from "@/components/WhatChanged";

// Everything that changed across your groups and friends (Sept 2026).
// Reached from Home → "See all activity"; replaces the old Expenses tab.
const PAGE = 50;

export default function ActivityPage() {
  const [, setLocation] = useLocation();
  const { meId, nameOf, groupNameOf, colorOf } = useActivityLookups();
  const [older, setOlder] = useState<ActivityLog[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [done, setDone] = useState(false);

  const { data: first = [], isLoading } = useQuery<ActivityLog[]>({
    queryKey: ["/api/activity", "page"],
    queryFn: async () => (await apiRequest("GET", `/api/activity?limit=${PAGE}`)).json(),
    enabled: !!meId,
  });
  const rows = [...first, ...older];

  const loadMore = async () => {
    const last = rows[rows.length - 1];
    if (!last) return;
    setLoadingMore(true);
    try {
      const next: ActivityLog[] = await (await apiRequest("GET", `/api/activity?limit=${PAGE}&before=${encodeURIComponent(last.createdAt)}`)).json();
      setOlder((o) => [...o, ...next]);
      if (next.length < PAGE) setDone(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const days: { label: string; rows: ActivityLog[] }[] = [];
  for (const r of rows) {
    const label = dayHeading(r.createdAt);
    const last = days[days.length - 1];
    if (last && last.label === label) last.rows.push(r);
    else days.push({ label, rows: [r] });
  }

  return (
    <div className="space-y-2 pb-24" data-testid="activity-page">
      <button onClick={() => setLocation("/")} className="flex items-center gap-1 text-sm text-muted-foreground py-2 -ml-1" aria-label="Back to Home">
        <ArrowLeft className="w-4 h-4" /> Home
      </button>
      <h1 className="font-serif text-5xl tracking-tight leading-none">Activity</h1>
      <p className="text-sm text-muted-foreground pb-2">Everything that changed, across every group and friend.</p>

      {!isLoading && rows.length === 0 && (
        <p className="text-sm text-muted-foreground py-10 text-center">Nothing yet. Adds, edits, deletes and settle-ups will show up here.</p>
      )}

      {days.map((d) => (
        <div key={d.label}>
          <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground mt-5 mb-2">{d.label}</p>
          <div className="rounded-[22px] bg-card px-3.5 py-1">
            {d.rows.map((r) => {
              const href = activityHref(r, meId);
              return (
                <ActivityRow
                  key={r.id}
                  line={describeActivity(r, meId, nameOf, groupNameOf)}
                  when={whenLabel(r.createdAt)}
                  initial={(r.userId === meId ? "Y" : r.userName.charAt(0)).toUpperCase()}
                  color={colorOf(r.userId)}
                  onClick={href ? () => setLocation(href) : undefined}
                />
              );
            })}
          </div>
        </div>
      ))}

      {rows.length >= PAGE && !done && (
        <button onClick={loadMore} disabled={loadingMore} className="w-full text-sm text-accent-foreground py-4">
          {loadingMore ? "Loading…" : "Show older"}
        </button>
      )}
    </div>
  );
}
